import { useState } from 'react';
import omit from 'lodash/omit';
import { useNavigate } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NotificationIcon from '@mui/icons-material/NotificationsActive';
import NotificationOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import config from '../../config/config';
import CustomCheckbox from '../common/CustomCheckbox';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import DateRangeFilter from '../common/DateRangeFilter';
import FindPatientForm from '../patients/FindPatientForm';
import SelectProviderInput from '../providers/SelectProviderInput';
import SelectLocationInput from '../locations/SelectLocationInput';
import ProviderChip from '../providers/ProviderChip';
import {getLeadStatuses,getReferralSourceCategories} from '../../helpers/selectOptions';
import LeadIcon from './LeadIcon';

const sorts = {
  '-date_created': 'Newest first',
  'date_created': 'Oldest first',
  // '-session__date': 'Session Date (Newest first)',
  // 'session__date': 'Session Date (Oldest first)',
};

export const SortButton = props => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = () => setAnchorEl(null);
  return (
      <Box className="sort-button-box">
      <Stack direction='row' spacing={1} alignItems='center'>
        <Button
          id='sort-menu-btn'
          variant='grayOutline'
          aria-controls={!!anchorEl ? 'sort-menu-btn' : undefined}
          aria-haspopup='true'
          aria-expanded={!!anchorEl ? 'true' : undefined}
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{boxShadow: config().styles.defaultBoxShadow}}
        >
          {/* <Typography variant='large500Tight' color={config().colors.gray}>
            {sorts[props.sort] || sorts['-date_created']}
          </Typography> */}
          <SwapVertIcon sx={{fontSize: '1.5rem', color: 'var(--primary)'}}/>
        </Button>
        <Menu
          id='sort-menu'
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          disableScrollLock={true}
        >
          {
            Object.keys(sorts).map(k =>
              <MenuItem
                onClick={e => {
                  props.onChange(e, k);
                  handleClose();
                }}
              >
                {sorts[k]}
              </MenuItem>
            )
          }
        </Menu>
      </Stack>
    </Box>
  );
};

const LeadFilters = props => {
  const {filters, showStatusFilter} = props;
  const navigate = useNavigate();
  return (
    <Grid container paddingTop={1.5} paddingBottom={1.5} fullWidth spacing={1}>
        <Grid item xs={4} md='auto'>
          <Stack direction='row' spacing={1} alignItems='center'>
            <IconButton
                color={props?.filters?.has_updates ? 'secondary' : 'primary'}
                sx={{margin: 0, padding: 0.15, paddingTop: 0.3}}
                onClick={() => (
                  props?.filters?.has_updates
                  ? props.onFilterChange(omit(filters, 'has_updates')) 
                  : props.onFilterChange({...filters, has_updates: true})
                )}
              >
                {
                  props?.filters?.has_updates
                  ? <NotificationIcon />
                  : <NotificationOutlinedIcon />
                }
              </IconButton>
              <DateRangeFilter 
                startDate={filters.start_time}
                endDate={filters.end_time}
                iconOnly
                includeMonthOptions
                isActive={filters.start_time || filters.end_time}
                onSubmit={(e, {startDate, endDate}) => {
                  let newFilters = omit(filters, 'start_time', 'end_time');
                  if (startDate) newFilters.start_time = startDate;
                  if (endDate) newFilters.end_time = endDate;
                  props.onFilterChange(newFilters);
                }}
              />
            </Stack>
        </Grid>
        <Grid item xs={6} md={2}>
          <Stack direction='row' spacing={1}>
            {
              filters.provider_id
              ? <ProviderChip 
                providerId={filters.provider_id} 
                onDelete={() => props.onFilterChange(omit(filters, 'provider_id'))}
                deleteIcon={<CloseIcon />}
              />
              : <Box sx={{width: {xs: '100%', md: '150px'}}}>
                <SelectProviderInput
                  label={null}
                  placeholder='Provider'
                  defaultValue={filters?.provider_id}
                  fullWidth={true}
                  onChange={e => {
                    if (e.target?.type === 'text') return () => true;
                    return (
                      e.target.value
                      ? props.onFilterChange({...filters, provider_id: e.target.value})
                      : props.onFilterChange(omit(filters, 'provider_id'))
                    );
                  }}
                />
              </Box>
            }
          </Stack>
        </Grid>
        {
          showStatusFilter && <Grid item xs={6} md={2}>
          {
            !filters.status 
            ? <Box sx={{width: {xs: '100%', md: '150px'}}}>
              <CustomSelect
                placeholder='Status'
                options={[
                  {value: 'ACTIVE', label: 'Active'},
                  ...getLeadStatuses()
                ]}
                fullWidth={true}
                onChange={e => {
                  if (e.target?.type === 'text') return () => true;
                  props.onFilterChange({...filters, status: e.target.value});
                }}
              />
            </Box>
            : <Chip 
              icon={<LeadIcon status={filters.status} />}
              label={getLeadStatuses().find(s => s.value === filters.status)?.label || 'Active Leads'}
              onDelete={() => props.onFilterChange(omit(filters, 'status'))}
              deleteIcon={<CloseIcon />}
              sx={{
                width: {xs: '100%', md: '150px'},
                paddingLeft: '0.5rem',
              }}
            />
          }
          </Grid>
        }
        <Grid item xs={6} md={2}>
          <CustomTextField
            placeholder='Patient Name'
            defaultValue={filters?.text || ''}
            sx={{
              width: {xs: '100%', md: '150px'},
            }}
            onChange={e => props.onFilterChange({...filters, text: e.target.value})}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          {
            filters.provider__facility_id
            ? <Chip
              label='Team'
              onDelete={() => props.onFilterChange(omit(filters, 'provider__facility_id'))}
              deleteIcon={<CloseIcon />}
              sx={{
                width: {xs: '100%', md: '150px'},
              }}
            />
            : <Box sx={{width: {xs: '100%', md: '150px'}}}>
              <SelectLocationInput
                label={null}
                placeholder='Team'
                defaultValue={filters?.provider__facility_id}
                fullWidth={true}
                onChange={e => {
                  if (e.target?.type === 'text') return () => true;
                  return (
                    e.target.value
                    ? props.onFilterChange({...filters, provider__facility_id: e.target.value})
                    : props.onFilterChange(omit(filters, 'provider__facility_id'))
                  );
                }}
              />
            </Box>
          }
        </Grid>
        <Grid item xs={6} md={2}>
          <Stack direction='row' spacing={1} alignItems='center'>
            {
              !filters.referral_category
              ? <Box sx={{width: {xs: '100%', md: '150px'}}}>
                  <CustomSelect
                  placeholder='Source Category'
                  options={getReferralSourceCategories()}
                  fullWidth={true}
                  onChange={e => {
                    if (e.target?.type === 'text') return () => true;
                    props.onFilterChange({...filters, referral_category: (e.target.value || '').toUpperCase()});
                  }}
                />
              </Box>
              : <Chip 
                label={getReferralSourceCategories().find(s => s.value.toUpperCase() === filters.referral_category.toUpperCase())?.label}
                onDelete={() => props.onFilterChange(omit(filters, 'referral_category'))}
                deleteIcon={<CloseIcon />}
                sx={{
                  width: {xs: '100%', md: '200px'},
                }}
              />
            }
          </Stack>
        </Grid>
        {
          filters?.id && <Grid item xs={6} md={2}>
            <Chip 
              color='secondary'
              label={`ID=${filters.id}`}
              onDelete={() => props.onFilterChange(omit(filters, 'id'))}
              deleteIcon={<CloseIcon />}
              sx={{
                width: {xs: '100%', md: '150px'},
              }}
            />
          </Grid>
        }
          {/* <CustomCheckbox
            label='Show Unpaid Only'
            checked={filters.unpaid || false}
            onChange={e => props.onFilterChange(
              e.target.checked
              ? {...filters, unpaid: e.target.checked}
              : omit(filters, 'unpaid')
              )}
              /> */}
        {/* <Grid item xs={6} md={2}>
          <SortButton
            sort={filters?.sort}
            onChange={(e, sort) => props.onFilterChange({...filters, sort})}
          />
        </Grid> */}
    </Grid>
  );
};

export default LeadFilters;